/**
 * ============================================================================
 * 퀴즈 이미지 업로드 및 텍스트 추출 서비스
 * ============================================================================
 * 
 * 주요 함수 요약
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 1. uploadImageForTextExtraction - 이미지 업로드 후 job_uuid 반환 (bookUuid 필요)
 * 2. getTextExtractionStatus      - 텍스트 추출 상태 조회
 * 3. pollTextExtraction           - 추출 완료까지 폴링 (최대 60회, 약 2분)
 * 4. processQuizImages            - 여러 이미지 일괄 처리 (메인 함수, bookUuid 필요)
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 처리 흐름
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 1단계: 이미지 업로드
 *   POST /api/viewer/files/upload
 *   - FormData: upload (파일), purpose ('TEXT_EXTRACT'), book_uuid (책 UUID)
 *   -> { job_uuid, file_uuid } 반환
 * 
 * 2단계: 텍스트 추출 상태 폴링
 *   GET /api/viewer/text-extractions/{job_uuid}
 *   -> status: PENDING -> PROCESSING/RUNNING -> DONE
 *   -> 최대 60회 재시도 (2초 간격, 약 2분)
 *   -> DONE 시 result_text 반환
 * 
 * 3단계: 모든 이미지 처리 완료 후 결과 집계
 *   -> extractedTexts 배열로 반환
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 폴링 방식 사용 이유
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * - 텍스트 추출은 비동기 작업으로 완료까지 시간이 걸립니다
 * - 서버에서 작업 완료를 알려주는 실시간 통신(WebSocket/SSE)이 없습니다
 * - 작업 완료 시점을 알 수 없으므로 주기적으로 상태를 확인해야 합니다
 * 
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * 사용 예시
 * ────────────────────────────────────────────────────────────────────────────
 * 
 * import { processQuizImages } from '@/front/services/quizImageService';
 * 
 * // 여러 이미지 처리 (bookUuid 필수)
 * const result = await processQuizImages(files, bookUuid, (current, total, status) => {
 *   console.log(`${status} (${current}/${total})`);
 * });
 * 
 * if (result.success) {
 *   console.log('추출된 텍스트:', result.extractedTexts);
 * }
 * 
 * // 결과 구조
 * // {
 * //   success: true,
 * //   extractedTexts: ['텍스트1', '텍스트2'],
 * //   failedCount: 0,
 * //   results: [{ jobUuid, fileUuid, status, resultText, errorMessage }]
 * // }
 * 
 * ============================================================================
 */
import apiClient from '../../core/http/axios';

// 텍스트 추출 상태
type ExtractionStatus = 'PENDING' | 'PROCESSING' | 'RUNNING' | 'DONE' | 'FAILED';

// 이미지 업로드 응답 타입
interface ImageUploadResponse {
  success: boolean;
  error: string | null;
  data: {
    file_uuid: string;
    job_uuid: string;
  };
  code: string | null;
}

// 텍스트 추출 응답 타입
interface TextExtractionResponse {
  success: boolean;
  error: string | null;
  data: {
    job_uuid: string;
    file_uuid: string;
    user_no: number;
    status: ExtractionStatus;
    result_text: string | null;
    error_message: string | null;
    created_at: string;
    updated_at: string;
  };
  code: string | null;
}

// 추출 결과 타입
export interface ExtractionResult {
  jobUuid: string;
  fileUuid: string;
  status: ExtractionStatus;
  resultText: string | null;
  errorMessage: string | null;
}

// 전체 처리 결과 타입
export interface QuizImageProcessResult {
  success: boolean;
  extractedTexts: string[];
  failedCount: number;
  results: ExtractionResult[];
}

/**
 * 이미지를 업로드하고 job_uuid를 반환합니다.
 * @param file 업로드할 이미지 파일
 * @param bookUuid 책 UUID
 * @returns job_uuid
 */
const uploadImageForTextExtraction = async (file: File, bookUuid: string): Promise<{ jobUuid: string; fileUuid: string }> => {
  const formData = new FormData();
  formData.append('upload', file);
  formData.append('purpose', 'TEXT_EXTRACT');
  formData.append('book_uuid', bookUuid);

  const response = await apiClient.post<ImageUploadResponse>(
    `/viewer/files/upload`,
    formData,
    {
      headers: {
        'accept': 'application/json',
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || '이미지 업로드에 실패했습니다.');
  }

  return {
    jobUuid: response.data.data.job_uuid,
    fileUuid: response.data.data.file_uuid,
  };
};

/**
 * 텍스트 추출 상태를 조회합니다.
 * @param jobUuid 작업 UUID
 * @returns 추출 상태 및 결과
 */
const getTextExtractionStatus = async (jobUuid: string): Promise<ExtractionResult> => {
  const response = await apiClient.get<TextExtractionResponse>(
    `/viewer/text-extractions/${jobUuid}`,
    {
      headers: {
        'accept': 'application/json',
      },
    }
  );

  if (!response.data.success || !response.data.data) {
    throw new Error(response.data.error || '텍스트 추출 상태 조회에 실패했습니다.');
  }

  const data = response.data.data;
  return {
    jobUuid: data.job_uuid,
    fileUuid: data.file_uuid,
    status: data.status,
    resultText: data.result_text,
    errorMessage: data.error_message,
  };
};

/**
 * 텍스트 추출이 완료될 때까지 폴링합니다.
 * 
 * 폴링 방식을 사용하는 이유:
 * - 텍스트 추출은 비동기 작업으로 완료까지 시간이 걸립니다
 * - 서버에서 작업 완료를 알려주는 실시간 통신(WebSocket/SSE)이 없습니다
 * - 작업 완료 시점을 알 수 없으므로 주기적으로 상태를 확인해야 합니다
 * 
 * @param jobUuid 작업 UUID
 * @param maxRetries 최대 재시도 횟수 (기본값: 60, 약 2분간 폴링)
 * @param intervalMs 폴링 간격 (기본값: 2000ms)
 * @returns 추출 결과
 */
const pollTextExtraction = async (
  jobUuid: string,
  maxRetries: number = 60,
  intervalMs: number = 2000
): Promise<ExtractionResult> => {
  let retries = 0;

  // console.log(`[Quiz Image] 텍스트 추출 폴링 시작 (job: ${jobUuid}, maxRetries: ${maxRetries})`);

  while (retries < maxRetries) {
    const result = await getTextExtractionStatus(jobUuid);
    const statusUpper = result.status.toUpperCase();

    // console.log(`[Quiz Image] 폴링 ${retries + 1}/${maxRetries} - 상태: ${result.status} (job: ${jobUuid})`);

    // DONE 상태 확인 (대소문자 무시)
    if (statusUpper === 'DONE') {
      // console.log(`[Quiz Image] 텍스트 추출 완료 (job: ${jobUuid})`);
      return result;
    }

    // FAILED 상태 확인 (대소문자 무시)
    if (statusUpper === 'FAILED') {
      // console.error(`[Quiz Image] 텍스트 추출 실패 (job: ${jobUuid}):`, result.errorMessage);
      return result;
    }

    // PENDING, PROCESSING, 또는 RUNNING 상태면 대기 후 재시도
    if (statusUpper === 'PENDING' || statusUpper === 'PROCESSING' || statusUpper === 'RUNNING') {
      // console.log(`[Quiz Image] 대기 중... (${intervalMs}ms 후 재시도)`);
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      retries++;
      continue;
    }

    // 예상치 못한 상태 값
    // console.warn(`[Quiz Image] 예상치 못한 상태 값: ${result.status} (job: ${jobUuid})`);
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    retries++;
  }

  // 최대 재시도 횟수 초과
  // console.error(`[Quiz Image] 텍스트 추출 시간 초과 (job: ${jobUuid}, 재시도: ${retries}회)`);
  throw new Error(`텍스트 추출 시간 초과 (job: ${jobUuid}, 재시도: ${retries}회)`);
};

/**
 * 여러 이미지를 업로드하고 텍스트를 추출합니다.
 * @param files 업로드할 이미지 파일 배열
 * @param bookUuid 책 UUID
 * @param onProgress 진행 상황 콜백 (선택)
 * @returns 처리 결과
 */
export const processQuizImages = async (
  files: File[],
  bookUuid: string,
  onProgress?: (current: number, total: number, status: string) => void
): Promise<QuizImageProcessResult> => {
  if (files.length === 0) {
    return {
      success: false,
      extractedTexts: [],
      failedCount: 0,
      results: [],
    };
  }

  const total = files.length;
  const results: ExtractionResult[] = [];
  const extractedTexts: string[] = [];
  let failedCount = 0;

  // console.log(`[Quiz Image] ${total}개의 이미지 처리 시작`);

  // 1단계: 모든 이미지 업로드
  const uploadPromises = files.map(async (file, index) => {
    try {
      onProgress?.(index + 1, total, `이미지 업로드 중... (${index + 1}/${total})`);
      const { jobUuid, fileUuid } = await uploadImageForTextExtraction(file, bookUuid);
      // console.log(`[Quiz Image] 이미지 ${index + 1} 업로드 완료 (job: ${jobUuid})`);
      return { jobUuid, fileUuid, index };
    } catch (error) {
      // console.error(`[Quiz Image] 이미지 ${index + 1} 업로드 실패:`, error);
      return { error, index };
    }
  });

  const uploadResults = await Promise.all(uploadPromises);

  // 업로드 성공한 작업들만 필터링
  const successfulUploads = uploadResults.filter(
    (r): r is { jobUuid: string; fileUuid: string; index: number } => 'jobUuid' in r
  );

  // console.log(`[Quiz Image] ${successfulUploads.length}/${total}개 업로드 성공`);

  // 2단계: 텍스트 추출 폴링
  const extractionPromises = successfulUploads.map(async ({ jobUuid, index }) => {
    try {
      onProgress?.(index + 1, total, `텍스트 추출 중... (${index + 1}/${total})`);
      const result = await pollTextExtraction(jobUuid);
      // console.log(`[Quiz Image] 이미지 ${index + 1} 텍스트 추출 완료 (status: ${result.status})`);
      return { result, index };
    } catch (error) {
      // console.error(`[Quiz Image] 이미지 ${index + 1} 텍스트 추출 실패:`, error);
      return { error, index };
    }
  });

  const extractionResults = await Promise.all(extractionPromises);

  // 결과 정리
  extractionResults.forEach(({ result, error, index }) => {
    if (result) {
      results.push(result);
      if (result.status === 'DONE' && result.resultText) {
        extractedTexts.push(result.resultText);
      } else {
        failedCount++;
      }
    } else {
      failedCount++;
    }
  });

  // 업로드 실패한 것도 실패 카운트에 추가 => 향후 실패했을때 처리 로직 필요
  failedCount += total - successfulUploads.length;

  const success = extractedTexts.length > 0;

  // console.log('='.repeat(50));
  // console.log('[Quiz Image] 전체 처리 완료');
  // console.log(`  - 성공: ${extractedTexts.length}개`);
  // console.log(`  - 실패: ${failedCount}개`);
  // console.log('='.repeat(50));
  // console.log('[Quiz Image] 추출된 텍스트:');
  // extractedTexts.forEach((text, idx) => {
  //   console.log(`\n--- 이미지 ${idx + 1} ---`);
  //   console.log(text);
  // });
  // console.log('='.repeat(50));

  return {
    success,
    extractedTexts,
    failedCount,
    results,
  };
};

// ============================================================================
// 퀴즈 채팅 API
// ============================================================================

/**
 * 퀴즈 채팅 API 응답 타입
 */
interface QuizChatResponse {
  success: boolean;
  error: string | null;
  data: {
    dialog_no: number;
    chat_no: number;
    message: string;
    data: string;
  };
  code: string | null;
}

/**
 * 퀴즈 채팅 요청 파라미터
 */
interface QuizChatRequest {
  query: string;
  subject: string;
  page: number;
  quiz_json: string;
  dialog_no: number;
}

/**
 * 퀴즈 채팅 API 호출
 * POST /viewer/books/{book_uid}/quiz/chat
 * 
 * @param bookUid - 책 UID
 * @param request - 채팅 요청 파라미터
 * @returns 채팅 응답 (dialog_no, chat_no, message)
 */
export const sendQuizChat = async (
  bookUid: string,
  request: QuizChatRequest
): Promise<{
  dialogNo: number;
  chatNo: number;
  message: string;
  data: string;
}> => {
  // console.log('[Quiz Chat] 요청:', {
  //   bookUid,
  //   query: request.query,
  //   subject: request.subject,
  //   page: request.page,
  //   dialogNo: request.dialog_no,
  //   quizJsonLength: request.quiz_json.length,
  // });

  try {
    const response = await apiClient.post<QuizChatResponse>(
      `/viewer/books/${bookUid}/quiz/chat`,
      {
        query: request.query,
        subject: request.subject,
        page: request.page,
        quiz_json: request.quiz_json,
        dialog_no: request.dialog_no,
      },
      {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        timeout: 120000, // 퀴즈 생성은 AI 처리로 시간이 오래 걸릴 수 있으므로 2분으로 설정
      }
    );

    // console.log('[Quiz Chat] 응답:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.error || '퀴즈 채팅 요청 실패');
    }

    return {
      dialogNo: response.data.data.dialog_no,
      chatNo: response.data.data.chat_no,
      message: response.data.data.message,
      data: response.data.data.data,
    };
  } catch (error: any) {
    // console.error('[Quiz Chat] 실패:', {
    //   message: error.message,
    //   response: error.response?.data,
    //   status: error.response?.status,
    // });
    throw error;
  }
};

// ============================================================================
// 퀴즈 생성 API (새로운 API)
// ============================================================================

/**
 * 퀴즈 범위 타입
 */
export type QuizScope = 'entire_book_pages' | 'current_chapter_pages' | 'current_page';

/**
 * 퀴즈 아이템 타입
 */
export interface QuizItem {
  quiz_uuid: string;
  question: string;
  correct_answer: 'O' | 'X';
  description: string;
  hint: string;
  difficulty: string;
  scope?: QuizScope;
  quiz_no: number;
}

/**
 * 퀴즈 생성 API 응답 타입
 */
interface CreateQuizzesResponse {
  success: boolean;
  error: string | null;
  data: {
    set_uuid: string;
    set_rev_no: number;
    quizzes: QuizItem[];
  };
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

/**
 * 퀴즈 생성 요청 파라미터
 */
export interface CreateQuizzesRequest {
  subject: string;
  page_idx: number;
}

/**
 * 퀴즈 생성 API 호출 결과
 */
export interface CreateQuizzesResult {
  quizzes: QuizItem[];
  setUuid: string;
  setRevNo: number;
}

/**
 * 퀴즈 생성 API 호출
 * POST /viewer/books/{book_uuid}/quizzes
 * 
 * @param bookUuid - 책 UUID
 * @param request - 퀴즈 생성 요청 파라미터
 * @returns 생성된 퀴즈 목록 및 set_uuid
 */
export const createQuizzes = async (
  bookUuid: string,
  request: CreateQuizzesRequest
): Promise<CreateQuizzesResult> => {
  // console.log('[Create Quizzes] 요청:', {
  //   bookUuid,
  //   subject: request.subject,
  //   page_idx: request.page_idx,
  // });

  // 요청 본문 데이터 준비
  const requestBody = {
    subject: request.subject,
    page_idx: request.page_idx,
  };

  // console.log('[Create Quizzes] 요청 본문 크기:', JSON.stringify(requestBody).length, 'bytes');

  try {
    // sendQuizChat과 동일한 방식으로 호출 (작동하는 API와 동일한 설정)
    const response = await apiClient.post<CreateQuizzesResponse>(
      `/viewer/books/${bookUuid}/quizzes`,
      requestBody,
      {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        timeout: 120000, // sendQuizChat과 동일한 타임아웃 설정 (2분)
      }
    );

    // console.log('[Create Quizzes] 응답:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.error || '퀴즈 생성 요청 실패');
    }

    return {
      quizzes: response.data.data.quizzes,
      setUuid: response.data.data.set_uuid,
      setRevNo: response.data.data.set_rev_no,
    };
  } catch (error: any) {
    console.error('[Create Quizzes] 실패:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      statusText: error.response?.statusText,
      responseData: error.response?.data,
      requestUrl: error.config?.url,
      requestMethod: error.config?.method,
      requestData: error.config?.data ? JSON.parse(error.config.data) : null,
    });
    
    // 502 오류에 대한 상세 정보 로깅
    if (error.response?.status === 502) {
      // console.error('[Create Quizzes] 502 Bad Gateway 오류 발생');
      console.error('요청 본문 크기:', JSON.stringify(requestBody).length, 'bytes');
    }
    
    throw error;
  }
};

// ============================================================================
// 퀴즈 교체 API
// ============================================================================

/**
 * 퀴즈 교체 요청 파라미터
 */
export interface ReplaceQuizzesRequest {
  set_uuid: string;
  subject: string;
  page_idx: number;
  quizzes: QuizItem[];
  replace_targets: string[];
}

/**
 * 퀴즈 교체 API 응답 타입
 */
interface ReplaceQuizzesResponse {
  success: boolean;
  error: string | null;
  data: {
    quizzes: QuizItem[];
  };
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

/**
 * 퀴즈 교체 API 호출
 * POST /viewer/books/{book_uuid}/quizzes/replace
 * 
 * @param bookUuid - 책 UUID
 * @param request - 퀴즈 교체 요청 파라미터
 * @returns 교체된 퀴즈 목록
 */
export const replaceQuizzes = async (
  bookUuid: string,
  request: ReplaceQuizzesRequest
): Promise<QuizItem[]> => {
  // console.log('[Replace Quizzes] 요청:', {
  //   bookUuid,
  //   set_uuid: request.set_uuid,
  //   subject: request.subject,
  //   page_idx: request.page_idx,
  //   quizzesCount: request.quizzes.length,
  //   replaceTargetsCount: request.replace_targets.length,
  // });

  const requestBody = {
    set_uuid: request.set_uuid,
    subject: request.subject,
    page_idx: request.page_idx,
    quizzes: request.quizzes,
    replace_targets: request.replace_targets,
  };

  // console.log('[Replace Quizzes] 요청 본문 크기:', JSON.stringify(requestBody).length, 'bytes');

  try {
    const response = await apiClient.post<ReplaceQuizzesResponse>(
      `/viewer/books/${bookUuid}/quizzes/replace`,
      requestBody,
      {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        timeout: 120000,
      }
    );

    // console.log('[Replace Quizzes] 응답:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.error || '퀴즈 교체 요청 실패');
    }

    return response.data.data.quizzes;
  } catch (error: any) {
    // console.error('[Replace Quizzes] 실패:', {
    //   message: error.message,
    //   code: error.code,
    //   status: error.response?.status,
    //   statusText: error.response?.statusText,
    //   responseData: error.response?.data,
    //   requestUrl: error.config?.url,
    //   requestMethod: error.config?.method,
    // });
    
    throw error;
  }
};

// ============================================================================
// 퀴즈 Delivery API
// ============================================================================

/**
 * 퀴즈 opened API 응답 타입
 */
interface QuizOpenedResponse {
  success: boolean;
  error: string | null;
  data: {
    delivery_uuid: string;
    state: string;
    opened_at: string;
  };
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

/**
 * 퀴즈 solved API 응답 타입
 */
interface QuizSolvedResponse {
  success: boolean;
  error: string | null;
  data: {
    delivery_uuid: string;
    state: string;
    solved_at: string;
    is_correct: boolean;
    elapsed_ms: number;
  };
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

/**
 * 퀴즈 opened API 호출
 * POST /api/viewer/{viewer_uuid}/quiz-deliveries/{delivery_uuid}/opened
 * 
 * @param viewerUuid - Viewer UUID (책 UUID)
 * @param deliveryUuid - Delivery UUID
 * @returns opened 응답 데이터
 */
export const markQuizOpened = async (
  viewerUuid: string,
  deliveryUuid: string
): Promise<QuizOpenedResponse['data']> => {
  // console.log('[Quiz Delivery] opened API 호출:', {
  //   viewerUuid,
  //   deliveryUuid,
  // });

  try {
    const response = await apiClient.post<QuizOpenedResponse>(
      `/viewer/${viewerUuid}/quiz-deliveries/${deliveryUuid}/opened`,
      {},
      {
        headers: {
          'accept': 'application/json',
        },
      }
    );

    // console.log('[Quiz Delivery] opened API 응답:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.error || '퀴즈 opened API 호출 실패');
    }

    return response.data.data;
  } catch (error: any) {
    // console.error('[Quiz Delivery] opened API 실패:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   responseData: error.response?.data,
    // });
    throw error;
  }
};

/**
 * 퀴즈 solved API 호출
 * POST /api/viewer/{viewer_uuid}/quiz-deliveries/{delivery_uuid}/solved
 * 
 * @param viewerUuid - Viewer UUID (책 UUID)
 * @param deliveryUuid - Delivery UUID
 * @param answer - 학생 답변 ('O' 또는 'X')
 * @returns solved 응답 데이터
 */
export const markQuizSolved = async (
  viewerUuid: string,
  deliveryUuid: string,
  answer: 'O' | 'X'
): Promise<QuizSolvedResponse['data']> => {
  // console.log('[Quiz Delivery] solved API 호출:', {
  //   viewerUuid,
  //   deliveryUuid,
  //   answer,
  // });

  try {
    const response = await apiClient.post<QuizSolvedResponse>(
      `/viewer/${viewerUuid}/quiz-deliveries/${deliveryUuid}/solved`,
      {
        answer: answer === 'O' ? 'O' : 'X',
      },
      {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );

    // console.log('[Quiz Delivery] solved API 응답:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.error || '퀴즈 solved API 호출 실패');
    }

    return response.data.data;
  } catch (error: any) {
    // console.error('[Quiz Delivery] solved API 실패:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   responseData: error.response?.data,
    // });
    throw error;
  }
};

// ============================================================================
// 미풀이 퀴즈 조회 API
// ============================================================================

/**
 * 미풀이 퀴즈 조회 API 응답 내 퀴즈 아이템
 */
export interface PendingQuizItem {
  delivery_uuid: string;
  room_uuid: string;
  set_uuid: string;
  set_rev_no: number;
  quiz_uuid: string;
  quiz_no: number;
  item_idx: number;
  question: string;
  choices: any | null;
  correct_answer: string;
  hint: string;
  difficulty: string;
  scope: string;
  sent_at: string;
  expires_at: string | null;
}

/**
 * 미풀이 퀴즈 조회 API 응답 타입
 */
interface PendingQuizzesResponse {
  success: boolean;
  error: string | null;
  data: {
    quizzes: PendingQuizItem[];
  };
  code: string | null;
  meta?: {
    elapsed_ms: number;
  };
}

/**
 * 미풀이 퀴즈 조회 API 호출
 * GET /api/viewer/{book_uuid}/quizzes/pending
 *
 * @param bookUuid - 책 UUID (viewer_uuid)
 * @param limit - 최대 조회 건수 (기본 50)
 * @returns 미풀이 퀴즈 목록
 */
export const fetchPendingQuizzes = async (
  bookUuid: string,
  limit: number = 50
): Promise<PendingQuizItem[]> => {
  // console.log('[Pending Quizzes] 조회 요청:', { bookUuid, limit });

  try {
    const response = await apiClient.get<PendingQuizzesResponse>(
      `/viewer/${bookUuid}/quizzes/pending`,
      {
        params: { limit },
        headers: {
          'accept': 'application/json',
        },
      }
    );

    // console.log('[Pending Quizzes] 응답:', {
    //   success: response.data.success,
    //   count: response.data.data?.quizzes?.length ?? 0,
    // });

    if (!response.data.success) {
      throw new Error(response.data.error || '미풀이 퀴즈 조회 실패');
    }

    return response.data.data.quizzes ?? [];
  } catch (error: any) {
    // console.error('[Pending Quizzes] 실패:', {
    //   message: error.message,
    //   status: error.response?.status,
    //   responseData: error.response?.data,
    // });
    throw error;
  }
};

export const quizImageService = {
  uploadImageForTextExtraction,
  getTextExtractionStatus,
  pollTextExtraction,
  processQuizImages,
  sendQuizChat,
  createQuizzes,
  replaceQuizzes,
  markQuizOpened,
  markQuizSolved,
  fetchPendingQuizzes,
};

export default quizImageService;

